export type LoanStage =
  | 'File Imported'
  | 'Setup'
  | 'Underwriting'
  | 'Suspended'
  | 'AWC'
  | 'CTC'
  | 'Closing'

export const LOAN_STAGES: LoanStage[] = [
  'File Imported',
  'Setup',
  'Underwriting',
  'Suspended',
  'AWC',
  'CTC',
  'Closing',
]

export type AlertType =
  | 'AWC Appraisal Back'
  | 'AWC No Appraisal Back'
  | 'Disclosures Out Not Signed'
  | 'Disclosures Signed Not Submitted'
  | 'Missing Documents'
  | 'Rate Lock Expiring'
  | 'Closing Date Approaching'

export const ALERT_TYPES: AlertType[] = [
  'AWC Appraisal Back',
  'AWC No Appraisal Back',
  'Disclosures Out Not Signed',
  'Disclosures Signed Not Submitted',
  'Missing Documents',
  'Rate Lock Expiring',
  'Closing Date Approaching',
]

export interface Loan {
  id: string
  borrowerName: string
  amount: number
  loanNumber: string
  loanOfficer: string
  teamLead: string
  divisionLead: string
  propertyAddress: string
  alerts: AlertType[]
  daysInStage: number
  stage: LoanStage
}

// Mock Hierarchy
export const DIVISION_LEADS = [
  'Sarah Jenkins',
  'Michael Chen',
  'David Rodriguez',
]

export const TEAM_LEADS: Record<string, string[]> = {
  'Sarah Jenkins': ['Amanda Smith', 'James Wilson'],
  'Michael Chen': ['Robert Taylor', 'Lisa Anderson', 'Kevin White'],
  'David Rodriguez': ['Maria Garcia', 'John Davis'],
}

export const LOAN_OFFICERS: Record<string, string[]> = {
  'Amanda Smith': ['Tom Harris', 'Emma Clark'],
  'James Wilson': ['Noah Martinez', 'Olivia Lewis'],
  'Robert Taylor': ['William Walker', 'Sophia Hall'],
  'Lisa Anderson': ['Mason Allen', 'Isabella Young'],
  'Kevin White': ['Ethan King', 'Mia Wright'],
  'Maria Garcia': ['Alexander Scott', 'Charlotte Green'],
  'John Davis': ['Daniel Adams', 'Amelia Baker'],
}

// Helper to generate random data
const getRandomItem = <T,>(arr: T[]): T =>
  arr[Math.floor(Math.random() * arr.length)]
const getRandomItems = <T,>(arr: T[], maxItems: number): T[] => {
  const count = Math.floor(Math.random() * (maxItems + 1))
  const shuffled = [...arr].sort(() => 0.5 - Math.random())
  return shuffled.slice(0, count)
}
const getRandomAmount = () => Math.floor(Math.random() * 500000) + 150000
const getRandomDays = () => Math.floor(Math.random() * 14) + 1

const FIRST_NAMES = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'William',
  'Elizabeth',
  'David',
  'Barbara',
  'Richard',
  'Susan',
  'Joseph',
  'Jessica',
  'Thomas',
  'Sarah',
  'Charles',
  'Karen',
]
const LAST_NAMES = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
]
const STREETS = [
  'Main St',
  'Oak Ln',
  'Pine Ave',
  'Maple Dr',
  'Cedar Ct',
  'Elm St',
  'Washington Blvd',
  'Lakeview Dr',
  'Sunset Blvd',
  'Park Ave',
]
const CITIES = [
  'Springfield',
  'Rivertown',
  'Lakeside',
  'Centerville',
  'Fairview',
  'Madison',
  'Georgetown',
  'Franklin',
  'Clinton',
  'Salem',
]

const generateMockLoans = (count: number): Loan[] => {
  const loans: Loan[] = []

  for (let i = 0; i < count; i++) {
    const divisionLead = getRandomItem(DIVISION_LEADS)
    const teamLead = getRandomItem(TEAM_LEADS[divisionLead])
    const loanOfficer = getRandomItem(LOAN_OFFICERS[teamLead])

    const borrowerName = `${getRandomItem(FIRST_NAMES)} ${getRandomItem(LAST_NAMES)}`
    const propertyAddress = `${Math.floor(Math.random() * 9000) + 100} ${getRandomItem(STREETS)}, ${getRandomItem(CITIES)}`

    loans.push({
      id: `LN-${10000 + i}`,
      borrowerName,
      amount: getRandomAmount(),
      loanNumber: `100${Math.floor(Math.random() * 900000) + 100000}`,
      loanOfficer,
      teamLead,
      divisionLead,
      propertyAddress,
      alerts: getRandomItems(ALERT_TYPES, 2), // 0 to 2 alerts
      daysInStage: getRandomDays(),
      stage: getRandomItem(LOAN_STAGES),
    })
  }

  return loans
}

export const MOCK_LOANS = generateMockLoans(45)
